import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import { Menu } from "lucide-react";
import socket, { connectSocket } from "../../socket";
import API_URL from "../../config";

const AdminLayout = () => {
    const [username, setUsername] = useState("");
    const [serverTime, setServerTime] = useState("");
    const [offset, setOffset] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Fetch the currently logged-in user from the backend
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await fetch(`${API_URL}/verify-token`, {
                    method: "GET",
                    credentials: "include", // Include cookies in the request
                });

                if (response.ok) {
                    const data = await response.json();
                    setUsername(data.username); // Assuming the backend returns the username
                }
            } catch (err) {
                console.error("Failed to fetch user:", err);
            }
        };

        fetchUser();
    }, []);

    // Handle logout via Socket.IO and backend
    const handleLogout = async () => {
        try {
            // Notify the backend via Socket.IO
            socket.emit("logout");

            // Call the logout endpoint to clear the cookie
            await fetch(`${API_URL}/logout`, {
                method: "POST",
                credentials: "include", // Include cookies in the request
            });

            // Redirect to the login page
            window.location.href = "/admin/login";
        } catch (err) {
            console.error("Failed to logout:", err);
        }
    };

    // Handle server time and socket connection status
    useEffect(() => {
        connectSocket(); // actively ensure connection is opened

        // Check the initial connection state
        setIsConnected(socket.connected);

        // Listen for server time on connection
        socket.on("serverTime", (serverTimestamp) => {
            const localTimestamp = Date.now();
            setOffset(serverTimestamp - localTimestamp); // Calculate offset
        });

        // Update connection status
        const handleConnect = () => setIsConnected(true);
        const handleDisconnect = () => setIsConnected(false);

        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);

        // Cleanup listeners on unmount
        return () => {
            socket.off("serverTime");
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
        };
    }, []);

    useEffect(() => {
        // Update the displayed server time every second
        const interval = setInterval(() => {
            const localTime = Date.now();
            const adjustedTime = new Date(localTime + offset);
            setServerTime(adjustedTime.toLocaleTimeString());
        }, 1000);

        return () => clearInterval(interval);
    }, [offset]);

    // Dynamic years for selector
    const currentYear = new Date().getFullYear();
    const availableYears = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];

    return (
        <div className="flex h-screen bg-gray-50 font-inter overflow-hidden relative">
            {/* Sidebar */}
            <AdminSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top Header */}
                <header className="h-16 flex items-center justify-between px-4 sm:px-6 bg-white border-b border-gray-200 z-10 shrink-0 shadow-sm">
                    {/* Left Section */}
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsSidebarOpen(true)}
                            className="md:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                        >
                            <Menu size={20} />
                        </button>
                        <div className="hidden sm:block text-sm font-medium text-gray-500">
                            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
                            {isConnected ? "Pripojený" : "Odpojený"}
                        </div>
                    </div>

                    {/* Center Section: Year Selector & Time */}
                    <div className="flex items-center gap-2 sm:gap-6">
                        <div className="flex items-center gap-2">
                            <label htmlFor="global-year-select" className="hidden sm:block text-sm font-semibold text-[#1E3A8A]">
                                Ročník:
                            </label>
                            <select
                                id="global-year-select"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                className="border border-gray-300 text-[#1E3A8A] font-bold rounded px-3 py-1 text-sm focus:outline-none focus:border-[#FACC15] focus:ring-1 focus:ring-[#FACC15]"
                            >
                                {availableYears.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <div className="text-lg font-bold font-montserrat text-[#1E3A8A] tracking-wider">
                            {serverTime}
                        </div>
                    </div>

                    {/* Right Section */}
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className="hidden md:block text-sm font-medium text-gray-700">
                            {username ? `${username}` : "..."}
                        </div>
                        <button
                            onClick={handleLogout}
                            className="bg-[#FACC15] hover:bg-yellow-500 text-[#1E3A8A] font-montserrat font-bold px-3 py-2 sm:px-4 rounded shadow-sm transition-colors text-[10px] sm:text-xs uppercase tracking-wide"
                        >
                            Odhlásiť
                        </button>
                    </div>
                </header>

                {/* Main Content Scrollable */}
                <main className="flex-1 overflow-y-auto p-6">
                    {/* Outlet will inject selectedYear to child components using Context */}
                    <Outlet context={{ selectedYear, setSelectedYear }} />
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
